import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { isE2E } from "@faire/retailer-visitor-shared/lib/isE2E";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { getSettingMarketingSMSEligibleCountries } from "@faire/retailer-visitor-shared/settings/getSettingMarketingSMSEligibleCountries";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import referralInviteClicked from "@faire/web-api--source/api/brand-lead/referral-invite-clicked/post";
import { useQuery as useBrandQuery } from "@faire/web-api--source/api/brand/brandToken/get-hooks";
import { QueryParameters as GetInvitingRetailersParameters } from "@faire/web-api--source/api/retailer-to-maker/inviting-retailers/get";
import { useQuery as useGetInvitingRetailersQuery } from "@faire/web-api--source/api/retailer-to-maker/inviting-retailers/get-hooks";
import { useQuery as useGetPublicRetailerQuery } from "@faire/web-api--source/api/retailer/public/retailerToken/get-hooks";
import { trackBrandLeadsBrandsFormMobileNavigationFirstBackClick } from "@faire/web-api--source/events/brandLeads/click/brandsFormMobileNavigationFirstBack";
import { trackBrandLeadsInboundBrandReferralLinkView } from "@faire/web-api--source/events/brandLeads/view/inboundBrandReferralLink";
import { IBrandLead } from "@faire/web-api--source/indigofair/data/IBrandLead";
import { IBrandLeadReferralInviteClickedRequest } from "@faire/web-api--source/indigofair/data/IBrandLeadReferralInviteClickedRequest";
import { route as brandAdApplicationRoute } from "@faire/web-api--source/routes/apply/ad";
import { route as brandToBrandApplyRoute } from "@faire/web-api--source/routes/apply/brandTokenOrAlias";
import { route as retailerToBrandApplyRoute } from "@faire/web-api--source/routes/apply/r/retailerTokenSuffix";
import { route as brandsHomeRoute } from "@faire/web-api--source/routes/brands";
import once from "lodash/once";
import { useCallback, useEffect, useMemo } from "react";
import { matchPath, useHistory } from "react-router-dom";
import { getBrandSignupAutoAcceptedCountriesOptions } from "@faire/retailer/features/MakerApplication/MakerApplicationCountries";
import { useMakerSignUpFormContextStore } from "./MakerSignUpFormStore";
import { MakerApplicationStages } from "./MakerSignUpState";
import { useMakerSignUpContextStore } from "./MakerSignUpStore";
var trackInviteClickedOnce = once(function(isM2M, retailerTokenFromPath, email, brandTokenOrAlias) {
    if (isM2M) {
        if (!brandTokenOrAlias) {
            return;
        }
        referralInviteClicked(IBrandLeadReferralInviteClickedRequest.build({
            brand_token_alias: brandTokenOrAlias,
            brand_email_address: email
        }));
        return;
    }
    if (!retailerTokenFromPath) {
        return;
    }
    referralInviteClicked(IBrandLeadReferralInviteClickedRequest.build({
        retailer_token: retailerTokenFromPath,
        brand_email_address: email
    }));
});
export var useMakerSignUp = function() {
    var _useMakerSignUpContextStore = _sliced_to_array(useMakerSignUpContextStore([
        "applicationStage"
    ]), 2), applicationStage = _useMakerSignUpContextStore[0].applicationStage, setMakerState = _useMakerSignUpContextStore[1];
    var _useMakerSignUpFormContextStore = _sliced_to_array(useMakerSignUpFormContextStore([
        "country",
        "email",
        "brandLeadToken",
        "_source"
    ]), 1), _useMakerSignUpFormContextStore_ = _useMakerSignUpFormContextStore[0], country = _useMakerSignUpFormContextStore_.country, email = _useMakerSignUpFormContextStore_.email, brandLeadToken = _useMakerSignUpFormContextStore_.brandLeadToken, _source = _useMakerSignUpFormContextStore_._source;
    var history = useHistory();
    var strictLocalize = useStrictLocalization().strictLocalize;
    var pathname = getLocationOrThrow().pathname;
    var tokenFromPath = pathname.split("/").pop();
    var retailerTokenFromPath = tokenFromPath ? "r_".concat(tokenFromPath.toLowerCase()) : null;
    var brandTokenOrAlias = tokenFromPath !== null && tokenFromPath !== void 0 ? tokenFromPath : undefined;
    var isAd = Boolean(matchPath(removeLocaleFromUrl(pathname), {
        path: removeLocaleFromUrl(brandAdApplicationRoute),
        exact: true
    }));
    var isR2M = Boolean(matchPath(removeLocaleFromUrl(pathname), {
        path: removeLocaleFromUrl(retailerToBrandApplyRoute),
        exact: true
    }));
    var isM2M = !isAd && Boolean(matchPath(removeLocaleFromUrl(pathname), {
        path: removeLocaleFromUrl(brandToBrandApplyRoute),
        exact: true
    }));
    var isReferral = isR2M || isM2M;
    // Initialize applicationStage only once
    useEffect(function() {
        var initialStage = isReferral || isAd ? MakerApplicationStages.LANDING : MakerApplicationStages.FORM;
        // Only set if it hasn't been initialized yet
        if (applicationStage === undefined) {
            setMakerState({
                applicationStage: initialStage
            });
        }
    }, [
        isReferral,
        isAd,
        applicationStage,
        setMakerState
    ]);
    var setApplicationStage = useCallback(function(stage) {
        setMakerState({
            applicationStage: stage
        });
    }, [
        setMakerState
    ]);
    var _useGetPublicRetailerQuery = useGetPublicRetailerQuery(retailerTokenFromPath !== null && retailerTokenFromPath !== void 0 ? retailerTokenFromPath : "", {
        enabled: Boolean(retailerTokenFromPath) && isReferral
    }), referringRetailerData = _useGetPublicRetailerQuery.data, isReferringRetailerLoading = _useGetPublicRetailerQuery.isLoading;
    var _useBrandQuery = useBrandQuery(tokenFromPath !== null && tokenFromPath !== void 0 ? tokenFromPath : "", undefined, {
        enabled: Boolean(tokenFromPath) && isM2M
    }), referringBrandData = _useBrandQuery.data, isReferringBrandLoading = _useBrandQuery.isLoading;
    var _useGetInvitingRetailersQuery = useGetInvitingRetailersQuery(GetInvitingRetailersParameters.build({
        brandLeadToken: brandLeadToken !== null && brandLeadToken !== void 0 ? brandLeadToken : ""
    }), {
        enabled: Boolean(brandLeadToken)
    }), referringRetailersData = _useGetInvitingRetailersQuery.data, isReferringRetailersLoading = _useGetInvitingRetailersQuery.isLoading;
    var isLoading = isReferringRetailerLoading || isReferringBrandLoading || isReferringRetailersLoading;
    useEffect(function() {
        if (isReferral && email) {
            trackInviteClickedOnce(isM2M, retailerTokenFromPath, email, brandTokenOrAlias);
        }
    }, [
        isReferral,
        email,
        isM2M,
        retailerTokenFromPath,
        brandTokenOrAlias
    ]);
    useEffect(function() {
        var _referringRetailerData_retailer;
        if ((referringRetailerData === null || referringRetailerData === void 0 ? void 0 : (_referringRetailerData_retailer = referringRetailerData.retailer) === null || _referringRetailerData_retailer === void 0 ? void 0 : _referringRetailerData_retailer.token) || (referringBrandData === null || referringBrandData === void 0 ? void 0 : referringBrandData.token)) {
            var _referringRetailerData_retailer1;
            var _referringRetailerData_retailer_token, _referringBrandData_token;
            trackBrandLeadsInboundBrandReferralLinkView((_referringRetailerData_retailer_token = referringRetailerData === null || referringRetailerData === void 0 ? void 0 : (_referringRetailerData_retailer1 = referringRetailerData.retailer) === null || _referringRetailerData_retailer1 === void 0 ? void 0 : _referringRetailerData_retailer1.token) !== null && _referringRetailerData_retailer_token !== void 0 ? _referringRetailerData_retailer_token : "", (_referringBrandData_token = referringBrandData === null || referringBrandData === void 0 ? void 0 : referringBrandData.token) !== null && _referringBrandData_token !== void 0 ? _referringBrandData_token : "");
        }
    }, [
        referringRetailerData,
        referringBrandData
    ]);
    var getSource = function() {
        if (_source) {
            return _source;
        }
        if (isAd) {
            return IBrandLead.Source.DIGITAL_ADS;
        }
        if (isR2M) {
            return IBrandLead.Source.RETAILER_REFERRAL;
        }
        if (isM2M) {
            return IBrandLead.Source.BRAND_REFERRAL;
        }
        return IBrandLead.Source.ORGANIC;
    };
    var source = getSource();
    var getReferrerInformation = function() {
        if (isR2M && (referringRetailerData === null || referringRetailerData === void 0 ? void 0 : referringRetailerData.retailer)) {
            return {
                name: referringRetailerData.retailer.name,
                image: referringRetailerData.retailer.profile_image
            };
        }
        if (isM2M && referringBrandData) {
            return {
                name: referringBrandData.name,
                image: referringBrandData.squared_image
            };
        }
        return null;
    };
    var referrerInformation = getReferrerInformation();
    var otherReferringRetailerCount = useMemo(function() {
        if (!isR2M || !retailerTokenFromPath) {
            return 0;
        }
        var _referringRetailersData_retailers_filter_length;
        return (_referringRetailersData_retailers_filter_length = referringRetailersData === null || referringRetailersData === void 0 ? void 0 : referringRetailersData.retailers.filter(function(retailer) {
            return retailer.retailer_token !== retailerTokenFromPath;
        }).length) !== null && _referringRetailersData_retailers_filter_length !== void 0 ? _referringRetailersData_retailers_filter_length : 0;
    }, [
        isR2M,
        referringRetailersData,
        retailerTokenFromPath
    ]);
    var toggleAcceptedTerms = useCallback(function() {
        setMakerState(function(prev) {
            return {
                acceptedTerms: !prev.acceptedTerms
            };
        });
    }, [
        setMakerState
    ]);
    var shouldShowSMSOptIn = useMemo(function() {
        var sessionCountry = getSessionCountry();
        var isE2EEnabled = isE2E();
        var eligibleCountries = getSettingMarketingSMSEligibleCountries();
        return isE2EEnabled || eligibleCountries.some(function(eligibleCountry) {
            return eligibleCountry === sessionCountry;
        });
    }, []);
    var onBackClick = useCallback(function() {
        trackBrandLeadsBrandsFormMobileNavigationFirstBackClick();
        history.push(brandsHomeRoute);
    }, [
        history
    ]);
    var countryDropdownOption = useMemo(function() {
        var options = getBrandSignupAutoAcceptedCountriesOptions(strictLocalize);
        var retailerIso = country === null || country === void 0 ? void 0 : country.isoCode;
        if (retailerIso && retailerIso !== "Other") {
            var optionArr = [];
            options.forEach(function(option) {
                if (option.value === retailerIso) {
                    optionArr = [
                        option
                    ].concat(_to_consumable_array(optionArr));
                } else {
                    optionArr.push(option);
                }
            });
            return optionArr;
        }
        return options;
    }, [
        country,
        strictLocalize
    ]);
    return {
        displaySource: _source,
        source: source,
        referrerInformation: referrerInformation,
        otherReferringRetailerCount: otherReferringRetailerCount,
        toggleAcceptedTerms: toggleAcceptedTerms,
        shouldShowSMSOptIn: shouldShowSMSOptIn,
        onBackClick: onBackClick,
        isLoading: isLoading,
        isAd: isAd,
        isM2M: isM2M,
        isR2M: isR2M,
        applicationStage: applicationStage,
        setApplicationStage: setApplicationStage,
        countryDropdownOption: countryDropdownOption,
        referringBrand: referringBrandData,
        referringRetailer: referringRetailerData === null || referringRetailerData === void 0 ? void 0 : referringRetailerData.retailer,
        setMakerState: setMakerState
    };
};
